// @src/pre-deployment/src/index.js v1.2
// v1.1: Entry point, enhanced error handling and loading states
// v1.2: Outer error boundary around App to show minimal message + retry on bootstrap/top-level errors (no blank screen)

import React from 'react';
import ReactDOM from 'react-dom/client';

import './index.css';
import App from './App';

// Minimal error boundary for bootstrap/top-level errors (no Router or service deps)
class BootstrapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.group('BootstrapErrorBoundary: uncaught error');
    console.error('Error:', error);
    console.log('Component stack:', errorInfo?.componentStack);
    console.groupEnd();

    // Send to pipeline via dynamic import to avoid pulling Firebase at bootstrap
    import('./components/ErrorHandling/AutomatedErrorSystem/RawErrorService').then(({ writeRawError }) => {
      writeRawError(error, {
        componentName: 'BootstrapErrorBoundary',
        componentStack: errorInfo?.componentStack,
        operationType: 'bootstrap_error'
      });
    }).catch((e) => console.error('BootstrapErrorBoundary: failed to log to RawErrorService', e));
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#333',
          backgroundColor: '#f5f5f5'
        }}>
          <p style={{ marginBottom: '1rem' }}>Something went wrong.</p>
          <button
            type="button"
            onClick={this.handleRetry}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '14px',
              cursor: 'pointer',
              backgroundColor: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: '4px'
            }}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// 🔧 BUNDLE ALL COMPONENTS - Ensures all components are included in build
// REMOVED: require.context was causing webpack to process VocabularyFeature as an asset/media file
// Components are now bundled via static imports in componentRegistry.js
// This prevents React Error #306 (ghost media file issue)


console.group('🚀 PRE-DEPLOYMENT: Application starting');
console.log('Port: 3003');
console.log('Environment:', process.env.NODE_ENV);
console.log('Error handling: BootstrapErrorBoundary (index) → SafeErrorBoundary (Routes) → RawErrorService; see ErrorHandling_README.md');
console.groupEnd();

// Check if root element exists
const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('❌ Root element not found!');
  document.body.innerHTML = '<div style="padding: 20px; text-align: center; color: red;">Error: Root element not found</div>';
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <BootstrapErrorBoundary>
        <App />
      </BootstrapErrorBoundary>
    </React.StrictMode>
  );
}
